import { parseDbTimestamp } from "@repo/shared";
import type {
  AvailableModelsResponse,
  CreateScheduledTaskRequest,
  ScheduledTask,
  ScheduledTaskRun,
  Translated,
  UpdateScheduledTaskRequest,
} from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  MessageSquare,
  Pause,
  Pencil,
  Play,
  Square,
  Trash2,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { TimePicker } from "@/components/ui/time-picker";
import { get } from "@/lib/api/_client";
import {
  cancelScheduledTask,
  createScheduledTask,
  deleteScheduledTask,
  listScheduledTaskRuns,
  listScheduledTasks,
  runScheduledTaskNow,
  updateScheduledTask,
} from "@/lib/api/scheduled-tasks";
import { useSidebar } from "@/lib/context/sidebar-context";
import { localDateTimeToUtcField } from "@/lib/date-utils";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./scheduler-panel.i18n";

type SchedulerT = Translated<typeof T>;

type ScheduleMode = "once" | "daily" | "weekly" | "hourly" | "interval";

type FormState = {
  id?: string;
  name: string;
  prompt: string;
  modelId: string;
  notify: boolean;
  timeoutMinutes: number;
  mode: ScheduleMode;
  time: string;
  weekday: string;
  minutePastHour: number;
  intervalMinutes: number;
  onceDate: string;
  onceTime: string;
};

const emptyForm = (): FormState => {
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
  const yyyy = tomorrow.getFullYear();
  const mm = String(tomorrow.getMonth() + 1).padStart(2, "0");
  const dd = String(tomorrow.getDate()).padStart(2, "0");
  return {
    name: "",
    prompt: "",
    modelId: "",
    notify: false,
    timeoutMinutes: 30,
    mode: "daily",
    time: "09:00",
    weekday: "1",
    minutePastHour: 0,
    intervalMinutes: 15,
    onceDate: `${yyyy}-${mm}-${dd}`,
    onceTime: "09:00",
  };
};

function formToRequest(form: FormState): { type: ScheduleMode; value: string } | null {
  if (form.mode === "daily") {
    if (!/^\d{2}:\d{2}$/.test(form.time)) return null;
    return { type: "daily", value: form.time };
  }
  if (form.mode === "weekly") return { type: "weekly", value: `${form.weekday}:${form.time}` };
  if (form.mode === "hourly") return { type: "hourly", value: String(form.minutePastHour) };
  if (form.mode === "interval") return { type: "interval", value: String(form.intervalMinutes) };
  if (form.mode === "once") {
    // Send an instant, not a zone-less wall-clock string (the API parsed that
    // in the host zone, QL-686). Built in the browser zone, while daily/weekly
    // are Europe/Oslo: the same for every tenant today (all CET); revisit if
    // a non-CET tenant gets the scheduler. `null` for an incomplete or
    // non-existent local time (see handleSave for the spring-forward gap).
    const value = localDateTimeToUtcField(form.onceDate, form.onceTime);
    return value ? { type: "once", value } : null;
  }
  return null;
}

function parseSchedule(task: ScheduledTask, base: FormState): FormState {
  const out = { ...base };
  if (task.scheduleType === "daily") {
    out.mode = "daily";
    out.time = task.scheduleValue;
  } else if (task.scheduleType === "weekly") {
    const parts = task.scheduleValue.split(":");
    out.mode = "weekly";
    out.weekday = parts[0] ?? "1";
    out.time = `${parts[1] ?? "09"}:${parts[2] ?? "00"}`;
  } else if (task.scheduleType === "hourly") {
    out.mode = "hourly";
    out.minutePastHour = parseInt(task.scheduleValue, 10) || 0;
  } else if (task.scheduleType === "interval") {
    out.mode = "interval";
    out.intervalMinutes = parseInt(task.scheduleValue, 10) || 15;
  } else if (task.scheduleType === "once") {
    out.mode = "once";
    // scheduleValue is a canonical UTC instant (QL-686). Local getters are
    // intentional here: prefilling the date/time inputs below with the
    // viewer's own local wall clock (QL-691).
    const d = parseDbTimestamp(task.scheduleValue);
    if (d) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");
      out.onceDate = `${yyyy}-${mm}-${dd}`;
      out.onceTime = `${hh}:${mi}`;
    }
  }
  return out;
}

function describeSchedule(
  task: ScheduledTask,
  t: SchedulerT,
  formatDateTime: (d: Date | null) => string,
): string {
  if (task.scheduleType === "daily") {
    return t.dailyAt(task.scheduleValue);
  }
  if (task.scheduleType === "weekly") {
    const [dow, h, m] = task.scheduleValue.split(":");
    const dayIdx = parseInt(dow ?? "1", 10) - 1;
    const day = t.weekdays[dayIdx] ?? dow ?? "";
    return t.weeklyAt(day, `${h}:${m}`);
  }
  if (task.scheduleType === "hourly") {
    return t.hourlyAt(task.scheduleValue);
  }
  if (task.scheduleType === "interval") {
    return t.everyMinutes(task.scheduleValue);
  }
  if (task.scheduleType === "once") {
    // The entered time (scheduleValue is a canonical instant, QL-686), not
    // nextRunAt, which can differ (legacy already-due rows). Tenant regional
    // format, viewer-local time (QL-108).
    return formatDateTime(parseDbTimestamp(task.scheduleValue) ?? parseDbTimestamp(task.nextRunAt));
  }
  return task.scheduleValue;
}

function formatRelative(iso: string | null, t: SchedulerT): string {
  if (!iso) return "—";
  const at = parseDbTimestamp(iso);
  if (!at) return "—";
  const ms = at.getTime() - Date.now();
  const abs = Math.abs(ms);
  const past = ms < 0;
  const min = Math.round(abs / 60_000);
  if (min < 1) return past ? t.justNow : "<1m";
  const span = (value: string) => (past ? t.ago(value) : t.inFuture(value));
  if (min < 60) return span(`${min}m`);
  const h = Math.round(min / 60);
  if (h < 24) return span(`${h}h`);
  const d = Math.round(h / 24);
  return span(`${d}d`);
}

function StatusIcon({
  status,
}: {
  status: ScheduledTask["lastRunStatus"] | ScheduledTask["status"];
}) {
  if (status === "running" || status === "queued")
    return <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />;
  if (status === "success" || status === "completed")
    return <CheckCircle2 className="h-3.5 w-3.5 text-success" />;
  if (status === "failed" || status === "timeout" || status === "interrupted")
    return <XCircle className="h-3.5 w-3.5 text-destructive" />;
  if (status === "cancelled") return <AlertCircle className="h-3.5 w-3.5 text-warning" />;
  return null;
}

export function SchedulerPanel() {
  const t = useT(T);
  const queryClient = useQueryClient();
  const { openAiAgentThread, activeTab, isOpen } = useSidebar();
  // Panels stay mounted (display:none) when hidden — without this gate the
  // 3s poll runs on every page forever and eats into the API rate limit.
  const isPanelVisible = isOpen && activeTab === "scheduler";
  const [editing, setEditing] = useState<FormState | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: list, isLoading } = useQuery({
    queryKey: ["scheduled-tasks"],
    queryFn: listScheduledTasks,
    enabled: isPanelVisible,
    refetchInterval: isPanelVisible ? 3000 : false,
  });

  const { data: models } = useQuery({
    queryKey: ["ai-models", "available"],
    queryFn: () => get<AvailableModelsResponse>("/internal/tenant/ai-models/models/available"),
    staleTime: 60 * 60 * 1000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["scheduled-tasks"] });

  const createMutation = useMutation({
    mutationFn: createScheduledTask,
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateScheduledTaskRequest }) =>
      updateScheduledTask(id, data),
    onSuccess: () => {
      setEditing(null);
      invalidate();
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteScheduledTask(id),
    onSuccess: invalidate,
  });
  const runNowMutation = useMutation({
    mutationFn: (id: string) => runScheduledTaskNow(id),
    onSuccess: invalidate,
  });
  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelScheduledTask(id),
    onSuccess: invalidate,
  });
  const togglePauseMutation = useMutation({
    mutationFn: ({ id, pause }: { id: string; pause: boolean }) =>
      updateScheduledTask(id, { status: pause ? "paused" : "active" }),
    onSuccess: invalidate,
  });

  const handleSave = () => {
    if (!editing) return;
    if (!editing.name.trim() || !editing.prompt.trim()) {
      alert(t.requiredFields);
      return;
    }
    const sched = formToRequest(editing);
    if (!sched) {
      // Date and time both filled in but rejected: a time skipped by the
      // spring-forward DST change.
      const gap = editing.mode === "once" && !!editing.onceDate && !!editing.onceTime;
      alert(gap ? t.nonexistentTime : t.requiredFields);
      return;
    }
    if (editing.id) {
      const data: UpdateScheduledTaskRequest = {
        name: editing.name,
        prompt: editing.prompt,
        scheduleType: sched.type,
        scheduleValue: sched.value,
        modelId: editing.modelId || null,
        notify: editing.notify,
        timeoutMinutes: editing.timeoutMinutes,
      };
      updateMutation.mutate({ id: editing.id, data });
    } else {
      const data: CreateScheduledTaskRequest = {
        name: editing.name,
        prompt: editing.prompt,
        scheduleType: sched.type,
        scheduleValue: sched.value,
        modelId: editing.modelId || undefined,
        notify: editing.notify,
        timeoutMinutes: editing.timeoutMinutes,
      };
      createMutation.mutate(data);
    }
  };

  const openEditor = (task?: ScheduledTask) => {
    if (!task) {
      const f = emptyForm();
      if (models?.defaultChatModelId) f.modelId = models.defaultChatModelId;
      setEditing(f);
      return;
    }
    let f = emptyForm();
    f.id = task.id;
    f.name = task.name;
    f.prompt = task.prompt;
    f.modelId = task.modelId ?? "";
    f.notify = task.notify;
    f.timeoutMinutes = task.timeoutMinutes;
    f = parseSchedule(task, f);
    setEditing(f);
  };

  const openThread = (threadId: string | null) => {
    if (!threadId) return;
    openAiAgentThread(threadId);
  };

  if (editing) {
    return (
      <EditorView
        form={editing}
        models={models?.models ?? []}
        t={t}
        onChange={setEditing}
        onSave={handleSave}
        onCancel={() => setEditing(null)}
        saving={createMutation.isPending || updateMutation.isPending}
      />
    );
  }

  const tasks = list?.tasks ?? [];

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 h-16">
        <h2 className="text-xl font-semibold truncate">{t.title}</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => openEditor()}
          className="text-xs gap-1.5"
        >
          {t.new}
        </Button>
      </div>
      <Separator />
      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> ...
          </div>
        ) : tasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center px-6 text-muted-foreground">
            <CalendarClock className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-sm">{t.empty}</p>
          </div>
        ) : (
          <ul className="divide-y">
            {tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                expanded={expandedId === task.id}
                onToggleExpand={() => setExpandedId(expandedId === task.id ? null : task.id)}
                onEdit={() => openEditor(task)}
                onDelete={() => {
                  if (confirm(t.deleteConfirm)) deleteMutation.mutate(task.id);
                }}
                onRunNow={() => runNowMutation.mutate(task.id)}
                onCancel={() => cancelMutation.mutate(task.id)}
                onTogglePause={() =>
                  togglePauseMutation.mutate({ id: task.id, pause: task.status !== "paused" })
                }
                onOpenThread={openThread}
                t={t}
              />
            ))}
          </ul>
        )}
      </ScrollArea>
    </div>
  );
}

function TaskRow({
  task,
  expanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onRunNow,
  onCancel,
  onTogglePause,
  onOpenThread,
  t,
}: {
  task: ScheduledTask;
  expanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onRunNow: () => void;
  onCancel: () => void;
  onTogglePause: () => void;
  onOpenThread: (id: string | null) => void;
  t: SchedulerT;
}) {
  const { formatDateTime } = useRegionalFormat();
  const isRunning = task.status === "running" || task.status === "queued";
  const isPaused = task.status === "paused";
  return (
    <li className="px-3 py-2.5">
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={onToggleExpand}
          className="mt-1 text-muted-foreground hover:text-foreground"
          aria-label={t.expand}
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <StatusIcon status={isRunning ? task.status : task.lastRunStatus} />
            <span className="text-sm font-medium truncate">{task.name}</span>
            {isPaused && (
              <span className="text-[10px] uppercase text-muted-foreground">{t.statusPaused}</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {describeSchedule(task, t, formatDateTime)}
          </div>
          <div className="text-[11px] text-muted-foreground/80 mt-0.5">
            {t.nextRun}: {formatRelative(task.nextRunAt, t)}
            {task.lastRunAt && (
              <>
                {" · "}
                {t.lastRun}: {formatRelative(task.lastRunAt, t)}
              </>
            )}
          </div>
        </div>
        {/* Vertical stack, so the tooltips open sideways for the same reason the
            sidebar rail's do. `left` rather than `right`: the stack is already at
            the panel's right edge. */}
        <div className="flex flex-col gap-1">
          {isRunning ? (
            <Button
              size="icon"
              variant="ghost"
              onClick={onCancel}
              tooltip={t.cancel}
              tooltipSide="left"
            >
              <Square className="h-3 w-3" />
            </Button>
          ) : (
            <Button
              size="icon"
              variant="ghost"
              onClick={onRunNow}
              tooltip={t.runNow}
              tooltipSide="left"
            >
              <Play className="h-3 w-3" />
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            onClick={onTogglePause}
            tooltip={isPaused ? t.resume : t.pause}
            tooltipSide="left"
          >
            <Pause className="h-3 w-3" />
          </Button>
          <Button size="icon" variant="ghost" onClick={onEdit} tooltip={t.edit} tooltipSide="left">
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="text-destructive hover:text-destructive/80"
            onClick={onDelete}
            tooltip={t.delete}
            tooltipSide="left"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
      {expanded && <RunsList taskId={task.id} t={t} onOpenThread={onOpenThread} />}
    </li>
  );
}

function RunsList({
  taskId,
  t,
  onOpenThread,
}: {
  taskId: string;
  t: SchedulerT;
  onOpenThread: (id: string | null) => void;
}) {
  const { activeTab, isOpen } = useSidebar();
  // Same visibility gate as the task list — hidden panels keep their tree
  // mounted, so an expanded row would otherwise poll forever.
  const isPanelVisible = isOpen && activeTab === "scheduler";
  const { data, isLoading } = useQuery({
    queryKey: ["scheduled-task-runs", taskId],
    queryFn: () => listScheduledTaskRuns(taskId, 10),
    enabled: isPanelVisible,
    refetchInterval: isPanelVisible ? 3000 : false,
  });
  const runs = data?.runs ?? [];

  if (isLoading) {
    return (
      <div className="mt-2 ml-6 text-xs text-muted-foreground flex items-center gap-1">
        <Loader2 className="h-3 w-3 animate-spin" /> ...
      </div>
    );
  }
  if (runs.length === 0) {
    return <div className="mt-2 ml-6 text-xs text-muted-foreground/80">{t.noRuns}</div>;
  }
  return (
    <div className="mt-2 ml-5 border-l pl-2 space-y-1">
      {runs.map((r) => (
        <RunRow key={r.id} run={r} t={t} onOpenThread={onOpenThread} />
      ))}
    </div>
  );
}

function RunRow({
  run,
  t,
  onOpenThread,
}: {
  run: ScheduledTaskRun;
  t: SchedulerT;
  onOpenThread: (id: string | null) => void;
}) {
  const dur =
    run.durationMs != null
      ? run.durationMs > 60_000
        ? `${(run.durationMs / 60_000).toFixed(1)}m`
        : `${(run.durationMs / 1000).toFixed(1)}s`
      : "—";
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <StatusIcon status={run.status} />
      <span className="text-muted-foreground tabular-nums">{formatRelative(run.startedAt, t)}</span>
      <span className="text-muted-foreground/80">· {dur}</span>
      {run.errorMessage && (
        <span className="text-destructive/80 truncate" title={run.errorMessage}>
          {run.errorMessage}
        </span>
      )}
      {run.threadId && (
        <button
          type="button"
          className="ml-auto text-primary hover:underline flex items-center gap-1"
          onClick={() => onOpenThread(run.threadId)}
        >
          <MessageSquare className="h-3 w-3" />
          {t.openThread}
        </button>
      )}
    </div>
  );
}

function EditorView({
  form,
  models,
  t,
  onChange,
  onSave,
  onCancel,
  saving,
}: {
  form: FormState;
  models: { id: string; name: string; providerName?: string }[];
  t: SchedulerT;
  onChange: (f: FormState) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const days = t.weekdays;
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    onChange({ ...form, [key]: value });

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 h-16">
        <h2 className="text-xl font-semibold truncate">{form.id ? t.edit : t.new}</h2>
      </div>
      <Separator />
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          <div className="space-y-2">
            <Label>{t.name}</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label>{t.prompt}</Label>
            <Textarea
              value={form.prompt}
              onChange={(e) => set("prompt", e.target.value)}
              rows={5}
            />
          </div>

          <div className="space-y-2">
            <Label>{t.schedule}</Label>
            <select
              value={form.mode}
              onChange={(e) => set("mode", e.target.value as ScheduleMode)}
              className="w-full h-8 border rounded px-2 text-sm bg-background"
            >
              <option value="once">{t.once}</option>
              <option value="daily">{t.daily}</option>
              <option value="weekly">{t.weekly}</option>
              <option value="hourly">{t.hourly}</option>
              <option value="interval">{t.interval}</option>
            </select>
          </div>

          {form.mode === "daily" && (
            <div className="space-y-1 w-full max-w-[180px]">
              <Label>{t.time}</Label>
              <TimePicker value={form.time} onChange={(v) => set("time", v)} />
            </div>
          )}

          {form.mode === "weekly" && (
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2 min-w-0">
                <Label>{t.weekday}</Label>
                <select
                  value={form.weekday}
                  onChange={(e) => set("weekday", e.target.value)}
                  className="w-full h-8 border rounded px-2 text-sm bg-background"
                >
                  {days.map((d, i) => (
                    <option key={d} value={String(i + 1)}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1 min-w-0">
                <Label>{t.time}</Label>
                <TimePicker value={form.time} onChange={(v) => set("time", v)} />
              </div>
            </div>
          )}

          {form.mode === "hourly" && (
            <div className="space-y-2 w-full max-w-[180px]">
              <Label>{t.minutePastHour}</Label>
              <Input
                type="number"
                min={0}
                max={59}
                value={form.minutePastHour}
                onChange={(e) => set("minutePastHour", parseInt(e.target.value, 10) || 0)}
              />
            </div>
          )}

          {form.mode === "interval" && (
            <div className="space-y-2 w-full max-w-[180px]">
              <Label>{t.intervalMin}</Label>
              <Input
                type="number"
                min={1}
                value={form.intervalMinutes}
                onChange={(e) => set("intervalMinutes", parseInt(e.target.value, 10) || 1)}
              />
            </div>
          )}

          {form.mode === "once" && (
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2 min-w-0">
                <Label>{t.date}</Label>
                <DatePicker value={form.onceDate} onChange={(v) => set("onceDate", v)} />
              </div>
              <div className="space-y-1 min-w-0">
                <Label>{t.time}</Label>
                <TimePicker value={form.onceTime} onChange={(v) => set("onceTime", v)} />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>{t.model}</Label>
            <select
              value={form.modelId}
              onChange={(e) => set("modelId", e.target.value)}
              className="w-full h-8 border rounded px-2 text-sm bg-background"
            >
              <option value="">{t.chooseModel}</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.providerName ? `${m.providerName} / ${m.name}` : m.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label>{t.timeoutMin}</Label>
            <Input
              type="number"
              min={1}
              max={240}
              value={form.timeoutMinutes}
              onChange={(e) => set("timeoutMinutes", parseInt(e.target.value, 10) || 30)}
            />
          </div>
        </div>
      </ScrollArea>
      <div className="flex gap-2 p-3 border-t">
        <Button variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>
          {t.cancel}
        </Button>
        <Button className="flex-1" onClick={onSave} loading={saving}>
          {form.id ? t.save : t.new}
        </Button>
      </div>
    </div>
  );
}
